import type { IconProps } from "../icons/Icon";
import type { StrictPaddingStyle } from "../util/types";

import { List, type ListItem, type ListAccessibilityLabelProps } from "../List";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";

import React, {
  type ComponentRef,
  type ReactNode,
  type Ref,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  StyleSheet,
  View,
  Pressable,
  type TextStyle,
  Platform,
  type AccessibilityRole,
} from "react-native";

export interface SelectListItemProps {
  /**
   * The text content of the list item
   */
  text: string;

  /**
   * Optional description text displayed below the main text
   */
  description?: string;

  /**
   * Optional icon to display before the text
   */
  icon?: ReactNode;

  /**
   * Callback function when the list item is pressed
   */
  onPress?: () => void;

  /**
   * Whether the list item is disabled
   */
  isDisabled?: boolean;

  /**
   * Whether the list item should use destructive styling
   */
  destructive?: boolean;

  /**
   * Whether the list item is selected
   */
  selected?: boolean;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;

  /**
   * Optional ref to the item's Pressable component for programmatic focus management
   */
  ref?: Ref<View>;
}

/**
 * A list item component that can display an optional icon and text, following the MADS design system.
 * @see https://www.figma.com/design/UQ0baUglOtMCWJ1jkwcSzW/Mobile-Apps-Design-System--MADS-?m=auto&node-id=10217-10370
 */
export function SelectListItem({
  text,
  description,
  icon,
  onPress,
  isDisabled = false,
  destructive = false,
  selected = false,
  sx,
  ref,
}: SelectListItemProps) {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          display: "flex",
          flexDirection: "row",
          alignItems: description ? "flex-start" : "center",
          paddingVertical: theme.spacing.s3,
          paddingHorizontal: theme.spacing.s5,
          opacity: isDisabled ? 0.4 : 1,
          backgroundColor: isHovered ? theme.colors.backgroundActionHover : theme.colors.background,
          borderRadius: theme.borderRadius.md,
        },
        content: {
          flex: 1,
          flexDirection: "row",
          alignItems: description ? "flex-start" : "center",
        },
        iconContainer: {
          marginRight: theme.spacing.s2,
        },
        textContainer: {
          flex: 1,
        },
      }),
    [theme, description, isHovered, isDisabled],
  );

  const textColor = destructive
    ? theme.colors.foregroundActionDestructive
    : theme.colors.foregroundActionStrong;

  const styledIcon = icon
    ? React.cloneElement(icon as React.ReactElement<IconProps>, {
        color: textColor,
        "aria-hidden": true,
      })
    : null;

  const fontWeight = selected ? theme.fontWeights.bold : theme.fontWeights.normal;

  return (
    <Pressable
      ref={ref}
      accessibilityRole="button"
      role={Platform.OS === "web" ? "option" : undefined}
      onPress={onPress}
      disabled={isDisabled}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      accessible={true}
      accessibilityState={{ disabled: isDisabled, selected }}
      aria-disabled={isDisabled}
      aria-selected={selected}
      style={[
        styles.container,
        sx,
        Platform.OS === "web" &&
          ({
            outlineStyle: isFocused ? "solid" : "none",
            outlineWidth: isFocused ? 2 : undefined,
            outlineColor: isFocused ? theme.colors.utilityFocus : undefined,
            outlineOffset: isFocused ? -2 : undefined,
          } as any),
      ]}
    >
      <View style={styles.content}>
        {styledIcon && <View style={styles.iconContainer}>{styledIcon}</View>}
        <View style={styles.textContainer}>
          <Text
            level={3}
            sx={{
              color: textColor,
              fontWeight: String(fontWeight) as TextStyle["fontWeight"],
            }}
          >
            {text}
          </Text>
          {description && (
            <Text
              level={2}
              sx={{
                color: textColor,
              }}
            >
              {description}
            </Text>
          )}
        </View>
      </View>
    </Pressable>
  );
}

export interface SelectListProps {
  /**
   * Array of items to display in the list
   */
  items: SelectListItemProps[];

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;

  /**
   * Test ID for the list component
   */
  testID?: string;

  /**
   * Accessible labels to describe parts of the list
   */
  a11yLabels?: ListAccessibilityLabelProps;

  /**
   * Whether the list scrolls its own content when it overflows. Defaults to
   * false: the list renders at full height and a scrolling ancestor (e.g. a
   * bottom sheet) handles overflow. Set true when the list sits in a
   * fixed/constrained-height container (e.g. a popover) that should scroll
   * its options internally.
   */
  scrollEnabled?: boolean;
}

/**
 * A list component that displays a collection of SelectListItems.
 * @see https://www.figma.com/design/UQ0baUglOtMCWJ1jkwcSzW/Mobile-Apps-Design-System--MADS-?m=auto&node-id=10217-10370
 */
export function SelectList({
  items,
  sx,
  testID,
  a11yLabels,
  scrollEnabled = false,
}: SelectListProps) {
  const itemRefs = useRef<(ComponentRef<typeof Pressable> | null)[]>([]);
  const count = items.length;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (count === 0) return;

      const key = e.key;
      const currentIndex = itemRefs.current.findIndex(
        (item) => (e.target as unknown as View) === item,
      );

      let direction: "next" | "prev" | null = null;
      let nextIndex: number | null = null;
      if (key === "ArrowDown" || key === "ArrowRight") {
        e.preventDefault();
        direction = "next";
        // Stop at the last item instead of wrapping around
        if (currentIndex >= count - 1) return;
        nextIndex = currentIndex + 1;
      } else if (key === "ArrowUp" || key === "ArrowLeft") {
        e.preventDefault();
        direction = "prev";
        // Stop at the first item instead of wrapping around
        if (currentIndex <= 0) return;
        nextIndex = currentIndex - 1;
      }

      if (direction === null || nextIndex === null) return;

      let checked = 0;
      while (checked < count && items[nextIndex]?.isDisabled) {
        nextIndex =
          direction === "next" ? Math.min(nextIndex + 1, count - 1) : Math.max(nextIndex - 1, 0);
        checked += 1;
      }
      const nextRef = itemRefs.current[nextIndex];

      if (nextRef && typeof nextRef.focus === "function" && !items[nextIndex]?.isDisabled) {
        nextRef.focus();
      }
    },
    [count, items],
  );

  itemRefs.current.length = items.length;

  const listData: ListItem[] = items.map((item, index) => {
    // Extract ref from item props to avoid it overriding the internal itemRefs
    // callback when spread. Both refs are merged into a single callback below.
    const { ref: externalRef, ...itemProps } = item;
    return {
      key: `${item.text}-${index}`,
      content: (
        <SelectListItem
          ref={(el) => {
            itemRefs.current[index] = el;
            if (typeof externalRef === "function") {
              (externalRef as (el: View | null) => void)(el);
            } else if (externalRef != null) {
              (externalRef as React.MutableRefObject<View | null>).current = el;
            }
          }}
          {...itemProps}
        />
      ),
      optionSelected: item.selected,
    };
  });

  return (
    <List
      data={listData}
      testID={testID}
      a11yLabels={a11yLabels}
      style={sx}
      scrollEnabled={scrollEnabled}
      focusable={false}
      {...(Platform.OS === "web" && {
        onKeyDown: handleKeyDown,
        containerProps: {
          accessibilityRole: "listbox" as unknown as AccessibilityRole,
        },
        useCustomListItem: true,
      })}
    />
  );
}
